import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AccountAdminCompanyCreateService } from 'private/app/services/account-admin/account-admin-company-create.service';
import { CompanyQueryService } from 'private/app/services/account-admin/company-query.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AccountAdminDetailsEditConfiguration, CompanyCategoryType } from '../../account-admin-company-details/company-query/company-query-configuration';
import { Option } from '../../../../../common/components/select/select.component';
import { AccountAdminFormResultMap, GroupNames, GroupOption } from 'private/app/models/account-admin-search';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { AccountStatusState } from 'common/store/account-status/account-status.state';
import { Validators } from '@angular/forms';
import { inputValidator } from 'private/app/services/account-admin/account-admin-edit-form.service';
import { BrandFamily } from 'common/models/brand.model';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'common/services/toast.service';
import { ClaimOption } from 'private/app/services/account-admin/company-relationship.service';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';

export enum ServicesType {
    WarrantyDistributor = 'Warranty Distributor'
}

@Component({
    selector: 'hvac-business-information',
    templateUrl: './business-information.component.html',
    styleUrls: ['./business-information.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BusinessInformationComponent implements OnInit, OnDestroy {
    @Select(AccountStatusState.getUserBrandFamily) userBrandFamily$: Observable<string | null>;
    @Input() brandList: Option[];
    @Input() isInternalSuperAdmin: boolean;

    public companyCreateForm = this.companyCreateService.companyCreateForm;
    public companyConfiguration: AccountAdminDetailsEditConfiguration;
    public claimOptions: Option[] = [];
    public companyCategoryType = CompanyCategoryType;
    public servicesType = ServicesType;
    public isCBP: boolean = false;
    public isICP: boolean = false;
    public isSoldToRequired$ = new BehaviorSubject<boolean>(false);
    public isServicesInvalid: boolean = false;
    public isServicesDisabled: boolean = false;
    public isCompanyCategoryTypeDomDist: boolean;
    public ngOnDestroy$ = new Subject();
    public groupNameOptions$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public groupNameOptionsForModal$: BehaviorSubject<GroupOption[]> = new BehaviorSubject<GroupOption[]>([]);
    formData: AccountAdminFormResultMap;

    constructor(
        public translateService: TranslateService,
        public companyQuery: CompanyQueryService,
        public companyCreateService: AccountAdminCompanyCreateService,
        public accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService,
        private readonly accountAdminApiService: AccountAdminApiService,
        private readonly toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.companyQuery.companyDetailsConfiguration.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((configuration) => {
            this.companyConfiguration = configuration;
            this.isCompanyCategoryTypeDomDist = this.isCompanyCategoryDistributorType(this.companyConfiguration.configType!);
            if (this.isInternalSuperAdmin && this.isCompanyCategoryTypeDomDist) {
                this.getGroupNames();
            }
        });

        this.companyCreateService.formData.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((formData) => {
            this.formData = formData;
        });

        this.companyCreateService.servicesOptionData.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((data) => {
            this.isServicesDisabled = Boolean(data.length);
        });
        this.updateClaimOptions();
        this.userBrandFamily$.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((res) => {
            this.isCBP = res?.toLowerCase() === BrandFamily.CBP;
            this.updateClaimOptions();
        });
        this.companyCreateService.idmRoles$.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((role) => {
            this.isICP = role === IdmUserRoles.INTERNALICP;
            this.updateClaimOptions();
        });
    }

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    public isSoldToRequired(): boolean {
        let servicesValue;
        if (this.companyCreateForm.controls.services.value !== '') {
            servicesValue = (this.companyCreateForm.controls.services.value.find((service: { name: ServicesType; }) => service.name === ServicesType.WarrantyDistributor));
        }
        const soldToRequired = ((this.companyConfiguration.configType === CompanyCategoryType.DOMESTIC_DISTRIBUTOR || this.companyConfiguration.configType === CompanyCategoryType.INTERNATIONAL_DISTRIBUTOR) && servicesValue);

        soldToRequired ? this.companyCreateForm.controls.soldTo.addValidators(Validators.required) : this.companyCreateForm.controls.soldTo.setValidators([inputValidator(10, false)]);
        this.companyCreateForm.controls.soldTo.updateValueAndValidity();

        return soldToRequired;
    }

    setManager(conatctType: string, event: Option[]) {
        if (!event) {
            return;
        }
        this.accountAdminCompanyUtilsService.updateCompanyContact(event, conatctType, this.companyCreateService.company);
    }

    onLanguageChange(lan: Option[]) {
        const placeholder = this.translateService.instant('ACCOUNT_ADMIN.COMPANY_CREATE.PLACEHOLDER_TEXT.LANGUAGE');
        if (Array.isArray(lan)) {
            this.companyCreateService.company.language = lan?.map((language: Option) => language.name).filter((language) => language !== placeholder);
        }
    }

    onClaimTypeChange(claimType: Option[]) {
        if (Array.isArray(claimType)) {
            this.companyCreateService.company.warrantyType = claimType[0].value ? claimType[0].name : '';
        }
    }

    onServicesChange(services: Option[]) {
        this.isServicesInvalid = false;
        this.isSoldToRequired$.next(this.isSoldToRequired());
        if (services.length === 1 && services[0].name === ServicesType.WarrantyDistributor && (this.companyConfiguration.configType === CompanyCategoryType.DOMESTIC_DISTRIBUTOR || this.companyConfiguration.configType === CompanyCategoryType.INTERNATIONAL_DISTRIBUTOR)) {
            this.isServicesInvalid = true;
        }
        this.companyCreateService.company.isAssignable = false;
        this.companyCreateService.showAccountAssignableError = false;
        if (Array.isArray(services)) {
            this.companyCreateForm.controls.claimType.setValue([]);
            this.companyCreateService.company.warrantyType = '';
            this.companyCreateService.company.companyServices = services.map((option) => ({
                id: option.value,
                code: this.accountAdminApiService.companyServices.filter((service) => service.id === option.value)[0].code,
                name: option.name
            }));
            const selectedOptionArray = this.companyQuery.mapCompanyServices(services).map((service) => service.name);
            this.companyQuery.isClaimTypeVisible(selectedOptionArray);
            if (this.companyQuery.configuration.formConfig.claimType?.view) {
                this.companyCreateForm.controls.claimType.setValidators([Validators.required]);
                this.companyCreateForm.controls.claimType.updateValueAndValidity();
                if (this.isCBP || this.isICP) {
                    this.applyDefaultClaimType();
                }
            }
        }
    }

    onBrandsChange(brands: Option[]) {
        if (Array.isArray(brands)) {
            this.companyCreateService.company.brands = brands.map((option) => ({
                code: option.value,
                name: option.name
            }));
        }
    }

    onParentCompanyChange(parentCompany: Option) {
        if (parentCompany) {
            this.companyCreateService.company.parentCompany = {
                name: parentCompany.name,
                id: parentCompany.value
            };
        }
    }

    getGroupNames(forceRefresh = false) {
        this.accountAdminApiService.getGroupNames(forceRefresh)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(
                (response: GroupNames[]) => {
                    const groupNameOptions: GroupOption[] = response.map((group) => ({
                        name: group.groupName,
                        value: group.id.toString(),
                        comments: group.comments
                    }));

                    this.groupNameOptionsForModal$.next(groupNameOptions);

                    const groupNameOptionsWithPlaceholder = [
                        {
                            name: this.translateService.instant('ACCOUNT_ADMIN.COMPANY_CREATE.PLACEHOLDER_TEXT.GROUP_NAME'),
                            value: ''
                        },
                        ...groupNameOptions
                    ];
                    this.groupNameOptions$.next(groupNameOptionsWithPlaceholder);
                },
                (error: HttpErrorResponse) => {
                    this.groupNameOptions$.next([]);
                    this.toastService.raiseDefaultErrorToast('AccountAdminCompanyDetailsComponent-getGroupNames', error.message);
                }
            );
    }

    onGroupCreated() {
        if (this.isInternalSuperAdmin) {
            this.getGroupNames(true);
        }
    }

    isCompanyCategoryDistributorType(companyCategory: string): boolean {
        return [CompanyCategoryType.DOMESTIC_DISTRIBUTOR.toString(), CompanyCategoryType.INTERNATIONAL_DISTRIBUTOR.toString(), CompanyCategoryType.MARINE_DISTRIBUTOR.toString()].includes(companyCategory);
    }

    private updateClaimOptions(): void {
        this.claimOptions = [...this.companyQuery.claimTypeOptions];
        if (this.isCBP) {
            this.claimOptions.pop();
        }
        else if (!this.isICP) {
            this.claimOptions.unshift({
                name: this.translateService.instant('ACCOUNT_ADMIN.SELECT_CLAIM_TYPE'),
                value: ''
            });
        }
    }

    private applyDefaultClaimType(): void {
        const defaultClaimType = this.claimOptions.find((option) => option.value === ClaimOption.ENTITLEMENT_AND_CLAIMS);
        if (!defaultClaimType) {
            return;
        }
        this.companyCreateForm.controls.claimType.setValue([defaultClaimType]);
        this.companyCreateService.company.warrantyType = defaultClaimType.name;
    }
}
